//@ts-nocheck
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api";
import { ls, useLnLanguage } from "../../utils/lnText";

const KR_FONT = "'Noto Sans KR', sans-serif";

const COLORS = {
  warmGold: "#C4A574",
  warmGoldDark: "#8B7355",
  bg: "linear-gradient(160deg, #FAF7F2 0%, #F0EBE3 100%)",
  card: "#fff",
  cardBorder: "#E8E2D9",
  cardBorderActive: "#C4A574",
  textDark: "#4A4238",
  textMuted: "#9CA3AF",
  textLight: "#8B7355",
  white: "#fff",
  shadow: "0 2px 12px rgba(139,115,85,0.08)",
  shadowLg: "0 4px 24px rgba(139,115,85,0.14)",
  resultBg: "rgba(196,165,116,0.06)",
};

// Korean insight cards mapping with icons
const KOREAN_INSIGHT_CARDS = [
  { id: "harmony", title: "두 사람의 분위기", icon: "💗" },
  { id: "timing", title: "흐름의 맞춤", icon: "🕐" },
  { id: "emotional_distance", title: "마음의 거리", icon: "🌊" },
  { id: "guidance", title: "조용한 조언", icon: "⭐" },
  { id: "summary", title: "부드러운 요약", icon: "💫" },
];

// Default content - only used as fallback while loading
const DEFAULT_CONTENT: Record<string, string> = {
  harmony: "",
  timing: "",
  emotional_distance: "",
  guidance: "",
  summary: "",
};

// Default score and graph - only used as fallback
const DEFAULT_SCORE = {
  value: 0,
  label: "",
};

const DEFAULT_GRAPH = {
  categories: [
    "감정 흐름",
    "마음의 리듬",
    "소통의 온도",
    "분위기 조화",
    "함께하는 시간",
  ],
  values: [0, 0, 0, 0, 0],
};

interface KoreaCompatibilityResultProps {
  selfData: any;
  partnerData: any;
  onBack?: () => void;
}

// Radar Chart Component
const RadarChart: React.FC<{
  categories: string[];
  values: number[];
  size?: number;
}> = ({ categories, values, size = 200 }) => {
  const center = size / 2;
  const maxRadius = size / 2 - 20;
  const angleStep = (2 * Math.PI) / categories.length;

  const getPoint = (value: number, index: number) => {
    const angle = index * angleStep - Math.PI / 2;
    const radius = (value / 100) * maxRadius;
    return {
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle),
    };
  };

  const points = values.map((v, i) => getPoint(v, i));
  const polygonPoints = points.map((p) => `${p.x},${p.y}`).join(" ");

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Background circles */}
      {[25, 50, 75, 100].map((level) => (
        <circle
          key={level}
          cx={center}
          cy={center}
          r={(level / 100) * maxRadius}
          fill="none"
          stroke="#E8E2D9"
          strokeWidth="1"
          strokeDasharray={level === 100 ? "none" : "4,4"}
        />
      ))}
      {/* Axis lines */}
      {categories.map((_, i) => {
        const p = getPoint(100, i);
        return (
          <line
            key={i}
            x1={center}
            y1={center}
            x2={p.x}
            y2={p.y}
            stroke="#E8E2D9"
            strokeWidth="1"
          />
        );
      })}
      {/* Data polygon */}
      <polygon
        points={polygonPoints}
        fill="rgba(196,165,116,0.3)"
        stroke={COLORS.warmGold}
        strokeWidth="2"
      />
      {/* Data points */}
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="4" fill={COLORS.warmGold} />
      ))}
      {/* Labels */}
      {categories.map((cat, i) => {
        const p = getPoint(115, i);
        return (
          <text
            key={i}
            x={p.x}
            y={p.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="10"
            fill={COLORS.textMuted}
            fontFamily={KR_FONT}
          >
            {cat}
          </text>
        );
      })}
    </svg>
  );
};

// Score Ring Component
const ScoreRing: React.FC<{ score: number; label: string }> = ({
  score,
  label,
}) => {
  const size = 120;
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div style={{ textAlign: "center" }}>
      <div
        style={{
          position: "relative",
          width: size,
          height: size,
          margin: "0 auto",
        }}
      >
        <svg width={size} height={size}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#E8E2D9"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={COLORS.warmGold}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: "stroke-dashoffset 1s ease-out" }}
          />
        </svg>
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
          }}
        >
          <div
            style={{ fontSize: 28, fontWeight: 700, color: COLORS.textDark }}
          >
            {score}
          </div>
        </div>
      </div>
      {label && (
        <div
          style={{
            fontSize: 13,
            color: COLORS.textMuted,
            marginTop: 8,
            fontFamily: KR_FONT,
            maxWidth: 140,
          }}
        >
          {label}
        </div>
      )}
    </div>
  );
};

const KoreaCompatibilityResult: React.FC<KoreaCompatibilityResultProps> = ({
  selfData,
  partnerData,
  onBack,
}) => {
  useLnLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [scoreData, setScoreData] = useState(DEFAULT_SCORE);
  const [graphData, setGraphData] = useState(DEFAULT_GRAPH);
  const [summary, setSummary] = useState<{
    positive: string;
    adjustment: string;
  }>({ positive: "", adjustment: "" });
  // Store the full response content
  const [content, setContent] = useState<Record<string, string>>({
    harmony: "",
    timing: "",
    emotional_distance: "",
    guidance: "",
    summary: "",
  });

  useEffect(() => {
    fetchResult();
  }, []);

  const fetchResult = async () => {
    setLoading(true);
    setError("");

    try {
      const userId = localStorage.getItem("userId");
      const token = localStorage.getItem("token");

      // Build message for Korea compatibility
      const selfSummary = [
        selfData.blood_type && `혈액형: ${selfData.blood_type}`,
        selfData.dob && `생년월일: ${selfData.dob}`,
        selfData.destiny_time && `시간: ${selfData.destiny_time}`,
        selfData.birth_city && `출생지: ${selfData.birth_city}`,
      ]
        .filter(Boolean)
        .join(", ");

      const partnerSummary = [
        partnerData.blood_type && `혈액형: ${partnerData.blood_type}`,
        partnerData.dob && `생년월일: ${partnerData.dob}`,
        partnerData.destiny_time && `시간: ${partnerData.destiny_time}`,
        partnerData.birth_city && `출생지: ${partnerData.birth_city}`,
      ]
        .filter(Boolean)
        .join(", ");

      const message = `두 사람의 궁합을 읽어주세요.\n나 (사용자): ${selfSummary}\n상대 (파트너): ${partnerSummary}`;

      const baseUrl = api.defaults.baseURL || "";
      const streamUrl = `${baseUrl}/backend/chat/create?stream=true`;
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      const requestData: any = {
        userMessage: message,
        categoryId: null,
        subCategoryId: null,
        userId: userId || undefined,
        korea3BoxSelf: selfData,
        korea3BoxPartner: partnerData,
        saveChat: false, // Korea Compatibility should not save chat history
      };

      const response = await fetch(streamUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(requestData),
      });

      if (!response.ok || !response.body) {
        throw new Error("Stream request failed");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullResponse = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          const raw = line.slice(5).trim();
          if (raw === "[DONE]") break;
          try {
            const parsed = JSON.parse(raw);
            // Handle different response formats
            if (parsed.text) {
              fullResponse += parsed.text;
            } else if (parsed.content || parsed.content_text) {
              fullResponse += parsed.content || parsed.content_text;
            } else if (parsed.choices && parsed.choices[0]?.delta?.content) {
              fullResponse += parsed.choices[0].delta.content;
            }
          } catch {
            // Ignore parsing errors for individual chunks
          }
        }
      }

      // Parse the AI response as JSON
      const { score, graph, summaryData, cards } =
        parseAndExtractContent(fullResponse);
      setScoreData(score);
      setGraphData(graph);
      setSummary(summaryData);
      setContent(cards);
    } catch (err: any) {
      console.error("Korea compatibility error:", err);
      setError("궁합 분석 중 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setLoading(false);
    }
  };

  // Parse the JSON response and extract all content
  const parseAndExtractContent = (
    rawResponse: string,
  ): {
    score: typeof DEFAULT_SCORE;
    graph: typeof DEFAULT_GRAPH;
    summaryData: typeof summary;
    cards: typeof content;
  } => {
    const score = { ...DEFAULT_SCORE };
    const graph = { ...DEFAULT_GRAPH };
    const summaryData = { positive: "", adjustment: "" };
    const cards: Record<string, string> = { ...DEFAULT_CONTENT };

    try {
      // Clean the response
      let cleaned = rawResponse
        .replace(/```json\n?/gi, "")
        .replace(/```\n?/g, "")
        .trim();

      // Try to find JSON object - look for {...} pattern
      let parsed = null;

      // Try direct JSON parse first
      try {
        parsed = JSON.parse(cleaned);
      } catch {
        // Try to find JSON in the response
        const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try {
            parsed = JSON.parse(jsonMatch[0]);
          } catch {
            // Try to fix common JSON issues
            const fixedJson = jsonMatch[0]
              .replace(/,\s*([}\]])/g, "$1")
              .replace(/([{,]\s*)(\w+)\s*:/g, '$1"$2":');
            try {
              parsed = JSON.parse(fixedJson);
            } catch (e2) {
              // console.log("Failed to parse JSON");
            }
          }
        }
      }

      if (!parsed) {
        // console.log("No valid JSON found. Raw response:", rawResponse.substring(0, 200));
        return { score, graph, summaryData, cards };
      }

      // Get pages array from various possible structures
      const pages =
        parsed.pages || parsed.data?.pages || parsed.result?.pages || [];

      // Find P1_KoreaCompatibility - score and graph
      const p1Page = pages.find(
        (p: any) =>
          p.pageId === "P1_KoreaCompatibility" ||
          p.pageId === "P1" ||
          p.title?.includes("궁합"),
      );

      if (p1Page) {
        const components = p1Page.components || p1Page;

        // Extract scoreGauge
        const sg = components.scoreGauge || components;
        if (sg?.value !== undefined) {
          score.value =
            typeof sg.value === "number" ? sg.value : parseInt(sg.value) || 0;
          score.label = sg.label || "";
        }

        // Extract lifeGraph
        const lg = components.lifeGraph || components;
        if (lg?.categories) {
          graph.categories = lg.categories;
          graph.values = Array.isArray(lg.value) ? lg.value : [];
        }

        // Extract summary
        const sum = components.summary;
        if (Array.isArray(sum)) {
          sum.forEach((item: any) => {
            if (item.type === "positive" || item.title?.includes("자연")) {
              summaryData.positive = item.text || "";
            } else if (
              item.type === "adjustment" ||
              item.title?.includes("차분하게") ||
              item.title?.includes("조용히")
            ) {
              summaryData.adjustment = item.text || "";
            }
          });
        }
      }

      // Find P2_DetailedInsights - cards
      const insightsPage = pages.find(
        (p: any) =>
          p.pageId === "P2_DetailedInsights" ||
          p.pageId === "P2" ||
          p.pageId === "P2_KoreaInsights" ||
          p.title?.includes("이야기") ||
          p.title?.includes("함께"),
      );

      let foundCards: any[] = [];
      if (insightsPage?.cards) {
        foundCards = insightsPage.cards;
      } else if (insightsPage?.components?.cards) {
        foundCards = insightsPage.components.cards;
      } else if (Array.isArray(insightsPage)) {
        foundCards = insightsPage;
      }

      if (foundCards.length > 0) {
        foundCards.forEach((card: any) => {
          if (card.id && (card.description || card.text || card.content)) {
            cards[card.id] = card.description || card.text || card.content;
          }
        });
      }

      // Fallback: check if cards are at root level or in a different structure
      const rootCards =
        parsed.cards || parsed.data?.cards || parsed.insights?.cards || [];
      if (Array.isArray(rootCards) && rootCards.length > 0) {
        rootCards.forEach((card: any) => {
          if (
            card.id &&
            (card.description || card.text) &&
            cards[card.id] === ""
          ) {
            cards[card.id] = card.description || card.text;
          }
        });
      }

      // Fallback: check direct keys for all 5 card IDs
      const directKeys = [
        "harmony",
        "timing",
        "emotional_distance",
        "guidance",
        "summary",
      ];
      directKeys.forEach((key) => {
        if (cards[key] === "" && (parsed[key] || parsed[key]?.description)) {
          cards[key] = parsed[key]?.description || parsed[key];
        }
      });

      // Fallback: search pages for any card with matching IDs
      if (Object.values(cards).every((v) => v === "")) {
        pages.forEach((page: any) => {
          const pageCards = page.cards || page.components?.cards || [];
          pageCards.forEach((card: any) => {
            if (
              card.id &&
              directKeys.includes(card.id) &&
              (card.description || card.text)
            ) {
              cards[card.id] = card.description || card.text;
            }
          });
        });
      }
    } catch (e) {
      console.error("Error parsing response:", e);
    }

    return { score, graph, summaryData, cards };
  };

  // Loading state
  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: COLORS.bg,
          fontFamily: KR_FONT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ marginBottom: 16 }}>
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                animate={{ y: [0, -8, 0] }}
                transition={{
                  duration: 0.9,
                  repeat: Infinity,
                  delay: i * 0.18,
                  ease: "easeInOut",
                }}
                style={{
                  display: "inline-block",
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: COLORS.warmGold,
                  margin: "0 4px",
                }}
              />
            ))}
          </div>
          <p
            style={{
              fontSize: 14,
              color: COLORS.textMuted,
              fontFamily: KR_FONT,
            }}
          >
            두 사람의 궁합을 읽고 있어요...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: COLORS.bg,
          fontFamily: KR_FONT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center", padding: "0 20px" }}>
          <p
            style={{
              fontSize: 14,
              color: COLORS.textMuted,
              marginBottom: 20,
              fontFamily: KR_FONT,
            }}
          >
            {error}
          </p>
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={() => {
              if (onBack) onBack();
              else navigate(-1);
            }}
            style={{
              padding: "12px 24px",
              borderRadius: 12,
              background: COLORS.warmGold,
              color: "#fff",
              fontSize: 14,
              fontWeight: 600,
              fontFamily: KR_FONT,
              border: "none",
              cursor: "pointer",
            }}
          >
            돌아아가기
          </motion.button>
        </div>
      </div>
    );
  }

  // Main result page
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        minHeight: "100vh",
        background: COLORS.bg,
        fontFamily: KR_FONT,
        padding: "20px 16px 40px",
      }}
    >
      <div style={{ maxWidth: 600, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              margin: "0 auto 14px",
              background:
                "linear-gradient(135deg, rgba(196,165,116,0.15), rgba(196,165,116,0.25))",
              border: "2px solid rgba(196,165,116,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 24,
            }}
          >
            🌙
          </div>
          <h1
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: COLORS.textDark,
              margin: "0 0 6px",
              fontFamily: KR_FONT,
            }}
          >
            두 사람의 궁합
          </h1>
          <p
            style={{
              fontSize: 12,
              color: COLORS.textMuted,
              margin: 0,
              fontFamily: KR_FONT,
            }}
          >
            {ls("Compatibility Result")}
          </p>
        </div>

        {/* Score and Radar Chart Section */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={{
            background: COLORS.card,
            borderRadius: 20,
            border: `1px solid ${COLORS.cardBorder}`,
            padding: 24,
            marginBottom: 20,
            boxShadow: COLORS.shadow,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
            {/* Score Ring */}
            <div style={{ flexShrink: 0 }}>
              <ScoreRing score={scoreData.value} label={scoreData.label} />
            </div>

            {/* Radar Chart */}
            <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
              <RadarChart
                categories={graphData.categories}
                values={graphData.values}
                size={160}
              />
            </div>
          </div>

          {/* Summary */}
          {(summary.positive || summary.adjustment) && (
            <div
              style={{
                marginTop: 20,
                paddingTop: 16,
                borderTop: `1px solid ${COLORS.cardBorder}`,
              }}
            >
              {summary.positive && (
                <div style={{ marginBottom: 12 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: COLORS.warmGold,
                      marginBottom: 4,
                      fontFamily: KR_FONT,
                    }}
                  >
                    자연스러운 부분
                  </div>
                  <p
                    style={{
                      fontSize: 13,
                      color: COLORS.textDark,
                      lineHeight: 1.6,
                      margin: 0,
                      fontFamily: KR_FONT,
                    }}
                  >
                    {summary.positive}
                  </p>
                </div>
              )}
              {summary.adjustment && (
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: COLORS.textMuted,
                      marginBottom: 4,
                      fontFamily: KR_FONT,
                    }}
                  >
                    차분하게 지켜보면 좋은 부분
                  </div>
                  <p
                    style={{
                      fontSize: 13,
                      color: COLORS.textDark,
                      lineHeight: 1.6,
                      margin: 0,
                      fontFamily: KR_FONT,
                    }}
                  >
                    {summary.adjustment}
                  </p>
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* 5 Insight Cards */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {KOREAN_INSIGHT_CARDS.map((card, index) => (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + index * 0.08 }}
              style={{
                background: COLORS.card,
                borderRadius: 16,
                border: `1px solid ${COLORS.cardBorder}`,
                padding: "20px 18px",
                boxShadow: COLORS.shadow,
              }}
            >
              {/* Card Header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <span style={{ fontSize: 24 }}>{card.icon}</span>
                <span
                  style={{
                    fontSize: 15,
                    fontWeight: 600,
                    color: COLORS.warmGold,
                    fontFamily: KR_FONT,
                  }}
                >
                  {card.title}
                </span>
              </div>
              {/* Card Content - Full text */}
              <p
                style={{
                  fontSize: 14,
                  color: COLORS.textDark,
                  lineHeight: 1.8,
                  margin: 0,
                  fontFamily: KR_FONT,
                }}
              >
                {content[card.id] || "분석 내용을 준비하고 있어요..."}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Back Button */}
        <div style={{ textAlign: "center", marginTop: 32 }}>
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={() => {
              if (onBack) onBack();
              else navigate(-1);
            }}
            style={{
              padding: "14px 32px",
              borderRadius: 14,
              background: COLORS.warmGold,
              color: "#fff",
              fontSize: 15,
              fontWeight: 600,
              fontFamily: KR_FONT,
              border: "none",
              cursor: "pointer",
              boxShadow: COLORS.shadowLg,
            }}
          >
            돌아아가기
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default KoreaCompatibilityResult;
